import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { Recognition, RecognitionState } from '../types/recognitionTypes';

const initialState: RecognitionState = {
  networkRecognitions: [],
  userRecognitions: [],
  loading: 'idle',
  anonymousRecognition: false,
};

const fetchUserRecognitions = createAsyncThunk<Recognition[]>(
  'recognition/fetchUserRecognitions',
  async (_, thunkApi) => {
    try {
      // CORRIGIDO: Adicionado a '/' inicial
      const { data } = await axios.get('/api/recognition/getRecognitionsByUser');
      if (data == null) return thunkApi.rejectWithValue(true);
      // console.log('User Recognitions fetched: ', data);
      return data;
    } catch (error) {
      return thunkApi.rejectWithValue(true);
    }
  }
);

const fetchNetworkRecognitions = createAsyncThunk<Recognition[]>(
  'recognition/fetchNetworkRecognitions',
  async (_, thunkApi) => {
    try {
      // CORRIGIDO: Adicionado a '/' inicial
      const { data } = await axios.get('/api/recognition/getRecognitionByNetwork');
      if (data == null) return thunkApi.rejectWithValue(true);
      // console.log('Network Recognitions fetched: ', data);
      return data;
    } catch (error) {
      return thunkApi.rejectWithValue(true);
    }
  }
);

export const deleteNetworkRecognition = createAsyncThunk<number, number>(
  'recognition/deleteNetworkRecognition',
  async (recognitionId, thunkApi) => {
    try {
      // CORRIGIDO: Adicionado a '/' inicial
      const { status } = await axios.delete('/api/recognition/deleteRecognition', {
        params: { recognitionId },
      });

      if (status === 200) {
        return recognitionId;
      }
      return thunkApi.rejectWithValue(false);
    } catch (error) {
      return thunkApi.rejectWithValue(false);
    }
  }
);

export const updateNetworkRecognition = createAsyncThunk<
  { recognitionId: number; name: string; description?: string },
  {
    recognitionId: number;
    name: string;
    description?: string;
    IconRecognitionImage?: File | null;
    iconMediaId?: number;
  }
>(
  'recognition/updateNetworkRecognition',
  async ({ recognitionId, name, description, IconRecognitionImage, iconMediaId }, thunkApi) => {
    try {
      const formData = new FormData();

      formData.append('RecognitionId', recognitionId.toString());
      formData.append('Name', name);

      if (description) {
        formData.append('Description', description);
      }

      if (iconMediaId && iconMediaId > 0) {
        formData.append('IconMediaId', iconMediaId.toString());
      }

      if (IconRecognitionImage) {
        formData.append('IconRecognitionImage', IconRecognitionImage);
      }

      // console.log('FormData entries: ', Object.fromEntries(formData.entries()));

      // CORRIGIDO: Adicionado a '/'
      const { status } = await axios.put('/api/recognition/updateRecognition', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (status >= 200 && status < 300) {
        thunkApi.dispatch(fetchNetworkRecognitions());
        return { recognitionId, name, description };
      }
      return thunkApi.rejectWithValue(false);
    } catch (error) {
      return thunkApi.rejectWithValue(false);
    }
  }
);

export const createNetworkRecognition = createAsyncThunk<
  boolean,
  { name: string; description?: string; IconRecognitionImage?: File | null }
>('recognition/createNetworkRecognition', async (item, thunkApi) => {
  try {
    const formData = new FormData();

    formData.append('Name', item.name);

    if (item.description) {
      formData.append('Description', item.description);
    }

    if (item.IconRecognitionImage) {
      formData.append('IconRecognitionImage', item.IconRecognitionImage);
    }

    const { status } = await axios.post('/api/recognition/insertRecognition', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (status >= 200 && status < 300) {
      thunkApi.dispatch(fetchNetworkRecognitions());
      return true;
    }
    return thunkApi.rejectWithValue(false);
  } catch (error) {
    return thunkApi.rejectWithValue(false);
  }
});

export const insertUserRecognition = createAsyncThunk<
  boolean,
  {
    RecognitionId: string | number;
    FromUserId: string | number;
    ToUserId: string | number;
    IsAnonymous?: boolean;
  }
>('recognition/insertUserRecognition', async (item, thunkApi) => {
  try {
    const formData = new FormData();

    formData.append('RecognitionId', String(item.RecognitionId));
    formData.append('FromUserId', String(item.FromUserId));
    formData.append('ToUserId', String(item.ToUserId));
    formData.append('IsAnonymous', String(item.IsAnonymous ?? false));

    const { status } = await axios.post('/api/recognition/insertUserRecognition', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (status >= 200 && status < 300) {
      thunkApi.dispatch(fetchNetworkRecognitions());
      return true;
    }

    return thunkApi.rejectWithValue({ message: 'Erro inesperado ao inserir reconhecimento' });
  } catch (error: any) {
    console.error('Erro ao inserir reconhecimento no Thunk:', error);

    return thunkApi.rejectWithValue({
      response: {
        data: error?.response?.data,
      },
      message: error?.message,
    });
  }
});

export const createNetworkRecognitions = createAsyncThunk<
  boolean,
  { name: string; description?: string; IconRecognitionImage?: File | null }[]
>('recognition/createNetworkRecognitions', async (items, thunkApi) => {
  try {
    const formData = new FormData();

    items.forEach((item, index) => {
      formData.append(`recognitions[${index}].Name`, item.name);

      if (item.description) {
        formData.append(`recognitions[${index}].Description`, item.description);
      }
      if (item.IconRecognitionImage) {
        formData.append(`recognitions[${index}].IconRecognitionImage`, item.IconRecognitionImage);
      }
    });

    // CORRIGIDO: Adicionado a '/' inicial
    const { status } = await axios.post('/api/recognition/insertRecognitions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (status >= 200 && status < 300) {
      thunkApi.dispatch(fetchNetworkRecognitions());
      return true;
    }
    return thunkApi.rejectWithValue(false);
  } catch (error) {
    return thunkApi.rejectWithValue(false);
  }
});

const recognitionReducer = createSlice({
  name: 'recognition',
  initialState,
  reducers: {
    setRecognition(state, action: PayloadAction<Recognition>) {
      const index = state.userRecognitions.findIndex(
        recognition => recognition.recognitionId === action.payload.recognitionId
      );
      if (index > -1) {
        state.userRecognitions[index] = action.payload;
        return;
      }
      state.userRecognitions.push(action.payload);
    },
    removeRecognition(state, action: PayloadAction<number>) {
      state.userRecognitions = state.userRecognitions.filter(
        recognition => recognition.recognitionId !== action.payload
      );
    },
    setAnonymousRecognition(state, action: PayloadAction<boolean>) {
      state.anonymousRecognition = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchUserRecognitions.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchUserRecognitions.fulfilled, (state, { payload }) => {
      state.userRecognitions = payload;
      state.loading = 'succeeded';
    });
    builder.addCase(fetchUserRecognitions.rejected, state => {
      state.loading = 'failed';
    });

    builder.addCase(fetchNetworkRecognitions.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchNetworkRecognitions.fulfilled, (state, { payload }) => {
      state.networkRecognitions = payload;
      state.loading = 'succeeded';
    });
    builder.addCase(fetchNetworkRecognitions.rejected, state => {
      state.loading = 'failed';
    });

    builder.addCase(deleteNetworkRecognition.fulfilled, (state, { payload }) => {
      state.networkRecognitions = state.networkRecognitions.filter(
        recognition => recognition.recognitionId !== payload
      );
    });

    builder.addCase(updateNetworkRecognition.fulfilled, (state, { payload }) => {
      const index = state.networkRecognitions.findIndex(
        recognition => recognition.recognitionId === payload.recognitionId
      );
      if (index !== -1) {
        state.networkRecognitions[index].name = payload.name;
        if (payload.description !== undefined) {
          state.networkRecognitions[index].description = payload.description;
        }
      }
    });

    builder.addCase(insertUserRecognition.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(insertUserRecognition.fulfilled, (state, { payload }) => {
      state.loading = 'succeeded';
    });
    builder.addCase(insertUserRecognition.rejected, state => {
      state.loading = 'failed';
    });
  },
});

export { fetchUserRecognitions, fetchNetworkRecognitions };
export const { setRecognition, removeRecognition, setAnonymousRecognition } =
  recognitionReducer.actions;

export default recognitionReducer.reducer;
